import styled from '@emotion/styled'
import { AnimatedTitle } from 'app/components/Common/Animation/AnimatedTitle'
import { FadeInUp } from 'app/components/Common/Animation/FadeInUp'
import { Arrow } from 'app/components/Common/Arrow'
import { Button, Props as ButtonProps } from 'app/components/Common/Button'
import { Image, Props as ImageProps } from 'app/components/Common/Image'
import { FlexBox } from 'app/components/Layout/FlexBox'
import { styles } from 'app/theme'
import { Media } from 'app/theme/media'
import { positionStyle } from 'app/utils/customFunctions'
import { motion } from 'framer-motion'
import { useKeenSlider } from 'keen-slider/react'
import React, { memo, useEffect, useState } from 'react'

export interface Props {
  cta?: ButtonProps
  images?: ImageProps[]
  label?: string
  title?: string
}

export const RoomsPreview = memo(function RoomsPreview({
  cta,
  images,
  label,
  title,
}: Props) {
  if (!images || images.length < 1) {
    return null
  }

  const [currentSlide, setCurrentSlide] = useState(0)
  const [details, setDetails] = useState<any>(null)

  const [slider, instanceRef] = useKeenSlider({
    defaultAnimation: {
      duration: 1800,
    },
    drag: false,
    loop: true,
    slideChanged(s) {
      setCurrentSlide(s.track.details.rel)
    },
    detailsChanged(s) {
      images.length > 1 ? setDetails(s.track.details) : null
    },
    breakpoints: {
      '(max-width: 1199px)': {
        drag: true,
      },
    },
  })

  useEffect(() => {
    document.addEventListener('keydown', function (e) {
      switch (e.key) {
        case 'ArrowLeft':
          instanceRef.current?.prev()
          break
        case 'ArrowRight':
          instanceRef.current?.next()
          break
      }
    })
  }, [])

  return (
    <Container>
      <Slider ref={slider}>
        {images.map((item, index) => {
          if (!item) return

          return (
            <Slide className="keen-slider__slide" key={index}>
              <Inner style={positionStyle(instanceRef, details, index)}>
                <Image {...item} />
              </Inner>
            </Slide>
          )
        })}
      </Slider>

      <Text>
        {label ? <Label tag="h2" text={label} /> : null}

        {title ? <Title tag="h3" text={title} /> : null}

        {cta ? (
          <FadeInUp>
            <CTA variant="flat-invert" {...cta} />
          </FadeInUp>
        ) : null}
      </Text>

      {images.length > 1 ? (
        <Arrows dial={5} row>
          <Arrow
            onClick={() => {
              instanceRef.current?.prev()
            }}
            variant="simple"
          />
          <Counter>{`${currentSlide + 1}/${images.length}`}</Counter>
          <Arrow
            direction="R"
            onClick={() => {
              instanceRef.current?.next()
            }}
            variant="simple"
          />
        </Arrows>
      ) : null}
    </Container>
  )
})

const Container = styled.section`
  margin-top: 11.25rem;
  padding: 0 5vw;
  position: relative;
  text-align: center;

  @media (max-width: 1199px) {
    margin-top: 5.25rem;
    padding: 0 2rem;
  }
`

const Slider = styled(motion.div)`
  aspect-ratio: 1.8;
  max-height: 45rem;
  display: flex;
  width: 100%;
  outline: none;
  overflow: hidden;
  position: relative;
  &:before {
    content: '';
    width: 100%;
    height: 100%;
    background: ${({ theme }) => theme.colors.variants.neutralDark1};
    opacity: 0.4;
    position: absolute;
    top: 0;
    left: 0;
    z-index: 2;
  }

  @media (max-width: 1199px) {
    aspect-ratio: 0.7;
    max-height: none;
  }
`

const Slide = styled.div`
  width: 100%;
  height: 100%;
  overflow: hidden;
  position: relative;
  transition: none;
  transition-duration: 1500ms;
`

const Inner = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  transition-timing-function: cubic-bezier(0.85, 0, 0.15, 1);

  & > div {
    height: 100%;
  }

  img {
    ${styles.img}
  }
`

const Text = styled.div`
  position: absolute;
  top: 50%;
  right: 1.5rem;
  left: 1.5rem;
  transform: translateY(-50%);
  z-index: 3;

  @media (max-width: 1199px) {
    right: 2rem;
    left: 2rem;
  }
`

const Label = styled(AnimatedTitle)`
  ${styles.label}

  color: ${({ theme }) => theme.colors.variants.neutralLight1};
`

const Title = styled(AnimatedTitle)`
  ${styles.title.large}

  color: ${({ theme }) => theme.colors.variants.neutralLight1};
`

const CTA = styled(Button)`
  margin-top: 2rem;
`

const Arrows = styled(FlexBox)`
  width: 100%;
  position: absolute;
  bottom: 2.25rem;
  left: 0;
  z-index: 2;
`

const Counter = styled.div`
  color: ${({ theme }) => theme.colors.variants.neutralLight1};
  font-family: ${({ theme }) => theme.fontFamily.paragraph};
  font-size: 0.875rem;
  font-variation-settings: 'wght' 300;
  line-height: 1.0625rem;
  margin: 0 2.25rem;
  pointer-events: none;
  transform: translateY(0.0625rem);
`
